import { ChangeDetectionStrategy, Component, forwardRef, input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

/** Large tap-card radio options — one column, generous targets. */
@Component({
  selector: 'ds-radio-group',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FieldErrorComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioGroupComponent),
      multi: true,
    },
  ],
  template: `
    <fieldset class="ds-field group" [attr.aria-describedby]="describedBy()">
      <legend class="ds-field__label">
        {{ label() }}
        @if (required()) {
          <span class="ds-field__required" aria-hidden="true">*</span>
        }
      </legend>
      @if (helperText()) {
        <p class="ds-field__helper" [id]="fieldId + '-helper'">{{ helperText() }}</p>
      }
      <div class="group__options">
        @for (option of options(); track option.value) {
          <label
            class="option"
            [class.option--selected]="option.value === value()"
            [class.option--disabled]="disabled() || (option.disabled ?? false)"
          >
            <input
              class="option__input"
              type="radio"
              [name]="fieldId"
              [value]="option.value"
              [checked]="option.value === value()"
              [disabled]="disabled() || (option.disabled ?? false)"
              [attr.aria-invalid]="errorText() ? 'true' : null"
              (change)="select(option.value)"
              (blur)="onTouched()"
            />
            <span class="option__circle" aria-hidden="true"></span>
            <span class="option__text">
              <span class="option__label">{{ option.label }}</span>
              @if (option.description) {
                <span class="option__desc">{{ option.description }}</span>
              }
            </span>
          </label>
        }
      </div>
      @if (errorText()) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ errorText() }}</ds-field-error>
      }
    </fieldset>
  `,
  styles: `
    .group {
      border: 0;
      margin: 0;
      padding: 0;
    }

    .group legend {
      padding: 0;
    }

    .group__options {
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
    }

    .option {
      display: flex;
      align-items: flex-start;
      gap: var(--space-3);
      min-height: 56px;
      padding: var(--space-3) var(--space-4);
      background: var(--color-surface);
      border: 2px solid var(--color-border-input);
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: border-color var(--motion-fast), background-color var(--motion-fast);

      &:hover:not(.option--disabled) {
        border-color: var(--color-primary);
      }

      &--selected {
        border-color: var(--color-primary);
        background: var(--color-primary-tint);
      }

      &--disabled {
        background: var(--color-surface-sunken);
        border-color: var(--color-border);
        cursor: not-allowed;

        .option__label,
        .option__desc {
          color: var(--color-text-disabled);
        }
      }
    }

    /* keep the native input for assistive tech; draw our own circle */
    .option__input {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      clip-path: inset(50%);
      overflow: hidden;
    }

    .option__circle {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      margin-top: 1px;
      border: 2.5px solid var(--color-border-input);
      border-radius: 50%;
      background: var(--color-surface);
      transition: border-color var(--motion-fast);
    }

    .option--selected .option__circle {
      border-color: var(--color-primary);
      border-width: 9px;
    }

    .option__input:focus-visible + .option__circle {
      outline: var(--focus-ring);
      outline-offset: var(--focus-offset);
    }

    .option__text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .option__label {
      font-size: var(--text-md);
      font-weight: var(--font-weight-bold);
      color: var(--color-text);
    }

    .option__desc {
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
    }
  `,
})
export class RadioGroupComponent extends FieldBase<string> {
  options = input.required<RadioOption[]>();

  protected select(next: string): void {
    this.value.set(next);
    this.onChange(next);
  }
}
